import { useEffect, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'wouter-preact'
import { Badge, Banner, Button, Card, EmptyState } from '../components/ui'
import {
  applyProposal,
  getConversationQuality,
  getKnowledge,
  getMe,
  getPlaybook,
  getProposals,
  getScores,
  revertProposal,
  setProposalStatus,
} from '../api/endpoints'
import { siteMeta } from '../utils/site'
import { timeAgo } from '../utils/time'
import type {
  ConversationScore,
  Playbook,
  Proposal,
  ProposalDimension,
  ProposalSeverity,
  SiteId,
} from '../types'

const DIMENSION_LABEL: Record<ProposalDimension, string> = {
  escalation: 'Escalation',
  grounding: 'Grounding',
  lead_capture: 'Lead capture',
  tone: 'Tone',
  coverage: 'Coverage',
}

/** Apply targets: the 11 playbook fields (mirrors the worker schema) + the knowledge doc. */
const APPLY_TARGETS: { value: string; label: string }[] = [
  { value: 'voice', label: 'Voice & tone' },
  { value: 'coreOffer', label: 'Core offer' },
  { value: 'notInScope', label: 'What we do NOT do' },
  { value: 'pricing', label: 'Pricing posture' },
  { value: 'disqualifiers', label: 'Disqualifying signals' },
  { value: 'commonConfusions', label: 'Common visitor confusions' },
  { value: 'alwaysEscalate', label: 'Always escalate' },
  { value: 'mustNeverPromise', label: 'Must never promise' },
  { value: 'proofPoints', label: 'Proof points' },
  { value: 'examples', label: 'Example exchanges' },
  { value: 'notes', label: 'Notes' },
  { value: 'knowledge', label: 'Knowledge doc (append)' },
]

/** Colour a 0–100 score: green healthy, amber shaky, red poor. */
function scoreColor(n: number | null): string {
  if (n === null) return 'var(--m-text-muted, #6b7280)'
  if (n >= 80) return 'var(--m-success, #15803d)'
  if (n >= 50) return 'var(--m-warn-ink, #b45309)'
  return 'var(--m-danger, #b91c1c)'
}

function severityDot(sev: ProposalSeverity): string {
  return sev === 'hard' ? 'var(--m-danger, #b91c1c)' : sev === 'soft' ? 'var(--m-warn-ink, #b45309)' : 'var(--m-text-muted, #6b7280)'
}

/**
 * Quality — the self-improvement loop made visible. Each scored conversation
 * (C1 reflection) is an accordion that expands to the improvement proposals
 * (C0) it generated, so the score and its fix live together rather than in two
 * disconnected lists. Proposals not tied to a shown conversation (insights,
 * gauntlet, older reflections) fall to an "Other proposals" section.
 * Applying edits is Tune's job (C2), later — here you can only dismiss.
 */
export function QualityPage() {
  const queryClient = useQueryClient()
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  const sites: SiteId[] = meQuery.data?.sites ?? []
  const [selectedSite, setSelectedSite] = useState<SiteId | 'all'>('all')

  useEffect(() => {
    if (sites.length === 1) setSelectedSite(sites[0])
  }, [sites])

  const siteArg = selectedSite === 'all' ? undefined : selectedSite

  // Deep link from a conversation ("Open in Quality →"): pin that
  // conversation's score + proposals at the top, fetched directly so it
  // shows even when it's outside the recent-scores window.
  const convParam = new URLSearchParams(window.location.search).get('conversation')
  const pinnedQuery = useQuery({
    queryKey: ['conv-quality', convParam],
    queryFn: () => getConversationQuality(convParam!),
    enabled: !!convParam,
  })

  const scoresQuery = useQuery({ queryKey: ['scores', selectedSite], queryFn: () => getScores(siteArg) })
  const proposalsQuery = useQuery({ queryKey: ['proposals', selectedSite], queryFn: () => getProposals(siteArg) })
  const appliedQuery = useQuery({
    queryKey: ['proposals-applied', selectedSite],
    queryFn: () => getProposals(siteArg, 'applied'),
  })

  const dismiss = useMutation({
    mutationFn: (id: string) => setProposalStatus(id, 'dismissed'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposals'] }),
  })

  const revert = useMutation({
    mutationFn: (id: string) => revertProposal(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals-applied'] })
      const site = appliedQuery.data?.proposals.find((p) => p.id === id)?.site
      if (site) {
        queryClient.invalidateQueries({ queryKey: ['playbook', site] })
        queryClient.invalidateQueries({ queryKey: ['knowledge', site] })
      }
    },
  })

  if (meQuery.isLoading) return <div style={{ padding: 24 }}>Loading…</div>
  if (sites.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState title="No sites assigned" subtitle="Ask an admin to grant access to a site." />
      </div>
    )
  }

  const summary = scoresQuery.data?.summary
  const recent = scoresQuery.data?.recent ?? []
  const proposals = proposalsQuery.data?.proposals ?? []
  const showSite = selectedSite === 'all'

  // Link proposals to conversations via evidence. A reflection proposal carries
  // its conversation id in `evidence`; match those against the shown scores so
  // each conversation can reveal the fixes it produced.
  const shownIds = new Set(recent.map((r) => r.conversationId))
  const proposalsByConv = new Map<string, Proposal[]>()
  const linkedIds = new Set<string>()
  for (const p of proposals) {
    for (const cid of p.evidence) {
      if (!shownIds.has(cid)) continue
      const arr = proposalsByConv.get(cid) ?? []
      arr.push(p)
      proposalsByConv.set(cid, arr)
      linkedIds.add(p.id)
    }
  }
  const otherProposals = proposals.filter((p) => !linkedIds.has(p.id))
  const dismissing = (id: string) => dismiss.isPending && dismiss.variables === id

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Quality</h1>
        {sites.length > 1 && (
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite((e.target as HTMLSelectElement).value as SiteId | 'all')}
            style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
          >
            <option value="all">All sites</option>
            {sites.map((s) => <option key={s} value={s}>{siteMeta(s).name}</option>)}
          </select>
        )}
      </div>

      {/* ── Pinned: the conversation this page was opened from ─────────────── */}
      {convParam && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>From this conversation</h2>
            <Link href={`/conversations/${convParam}`} style={{ fontSize: 12, color: 'var(--m-action, #4f46e5)', textDecoration: 'none' }}>
              View transcript →
            </Link>
          </div>
          {pinnedQuery.isLoading ? (
            <div style={{ color: 'var(--m-text-muted, #6b7280)', fontSize: 14 }}>Loading…</div>
          ) : !pinnedQuery.data?.score ? (
            <div style={{ color: 'var(--m-text-muted, #6b7280)', fontSize: 14 }}>
              Not scored yet — scores land after the conversation closes and the hourly reflection pass runs.
            </div>
          ) : (
            <ScoreAccordion
              score={pinnedQuery.data.score}
              showSite
              proposals={pinnedQuery.data.proposals}
              onDismiss={(id) => dismiss.mutate(id)}
              dismissing={dismissing}
              defaultOpen
            />
          )}
        </Card>
      )}

      {/* ── Conversation health (C1) — each row expands to its fixes (C0) ───── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Conversation health</h2>
          <span style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
            auto-scored after each conversation — expand a row to see its proposed fixes
          </span>
        </div>
        {dismiss.isError && <Banner variant="error">Couldn't update that proposal.</Banner>}
        {scoresQuery.isLoading ? (
          <div style={{ color: 'var(--m-text-muted, #6b7280)', fontSize: 14 }}>Loading…</div>
        ) : !summary || summary.count === 0 ? (
          <div style={{ color: 'var(--m-text-muted, #6b7280)', fontSize: 14 }}>
            No conversations scored yet. Scores appear after conversations close and the hourly reflection pass runs.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 16 }}>
              <Stat label="Avg score" value={summary.avgComposite ?? '—'} color={scoreColor(summary.avgComposite)} big />
              <Stat label="Scored" value={summary.count} />
              <Stat label="Fabrications" value={summary.fabrications} color={summary.fabrications > 0 ? 'var(--m-danger, #b91c1c)' : undefined} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recent.slice(0, 25).map((s) => (
                <ScoreAccordion
                  key={s.conversationId}
                  score={s}
                  showSite={showSite}
                  proposals={proposalsByConv.get(s.conversationId) ?? []}
                  onDismiss={(id) => dismiss.mutate(id)}
                  dismissing={dismissing}
                />
              ))}
            </div>
          </>
        )}
      </Card>

      {/* ── Proposals not tied to a shown conversation ─────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Other proposals</h2>
          <span style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
            {proposals.length} open total · these are from insights, gauntlet, or older conversations
          </span>
        </div>
        {proposalsQuery.isLoading ? (
          <div style={{ color: 'var(--m-text-muted, #6b7280)', fontSize: 14 }}>Loading…</div>
        ) : otherProposals.length === 0 ? (
          <EmptyState title="Nothing else open 🎉" subtitle="Every open proposal is linked to a conversation above." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {otherProposals.map((p) => (
              <ProposalRow key={p.id} proposal={p} showSite={showSite} onDismiss={() => dismiss.mutate(p.id)} dismissing={dismissing(p.id)} />
            ))}
          </div>
        )}
      </Card>

      {/* ── Recently applied — the audit trail, with one-click revert ───────── */}
      {(appliedQuery.data?.proposals.length ?? 0) > 0 && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Recently applied</h2>
            <span style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
              live in the playbook/knowledge — re-run the golden gate before relying on changed answers
            </span>
          </div>
          {revert.isError && <Banner variant="error">Couldn't revert that change.</Banner>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {appliedQuery.data!.proposals.slice(0, 20).map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--m-border, #e5e7eb)', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.4 }}>{p.summary}</div>
                  <div style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)', marginTop: 2 }}>
                    {showSite && <>{siteMeta(p.site).name} · </>}
                    {p.targetField && <>target <code style={{ fontFamily: 'ui-monospace, monospace' }}>{p.targetField}</code> · </>}
                    applied {p.appliedAt ? timeAgo(p.appliedAt) : ''} by {p.appliedBy ?? '—'}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => revert.mutate(p.id)}
                  loading={revert.isPending && revert.variables === p.id}
                >
                  Revert
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  )
}

function Stat({ label, value, color, big }: { label: string; value: string | number; color?: string; big?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: big ? 34 : 22, fontWeight: 700, lineHeight: 1, color: color ?? 'var(--m-text, #111827)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)', marginTop: 4 }}>{label}</div>
    </div>
  )
}

function DimChip({ label, value }: { label: string; value: number | null }) {
  return (
    <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>
      {label}{' '}
      <span style={{ color: scoreColor(value), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value === null ? 'N/A' : value}
      </span>
    </span>
  )
}

/** A scored conversation that expands to reveal the proposals it generated. */
function ScoreAccordion({
  score,
  showSite,
  proposals,
  onDismiss,
  dismissing,
  defaultOpen = false,
}: {
  score: ConversationScore
  showSite: boolean
  proposals: Proposal[]
  onDismiss: (id: string) => void
  dismissing: (id: string) => boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const hasFixes = proposals.length > 0

  return (
    <div style={{ border: '1px solid var(--m-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o) } }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', cursor: 'pointer', background: open ? 'var(--m-bg-subtle, #f9fafb)' : 'transparent' }}
      >
        <span style={{ width: 14, color: 'var(--m-text-muted, #9ca3af)', fontSize: 11, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▶</span>
        <div style={{ width: 40, textAlign: 'center', fontSize: 18, fontWeight: 700, color: scoreColor(score.composite), fontVariantNumeric: 'tabular-nums' }}>
          {score.composite}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {showSite && <span style={{ fontSize: 11, fontWeight: 600 }}>{siteMeta(score.site).name}</span>}
          <DimChip label="esc" value={score.escalation} />
          <DimChip label="grnd" value={score.grounding} />
          <DimChip label="lead" value={score.leadApplicable ? score.leadCapture : null} />
          <DimChip label="tone" value={score.tone} />
          {score.fabrication && <Badge variant="error">fabrication</Badge>}
          {hasFixes && (
            <Badge variant={proposals.some((p) => p.severity === 'hard') ? 'error' : 'warn'}>
              {proposals.length} {proposals.length === 1 ? 'fix' : 'fixes'}
            </Badge>
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>{timeAgo(score.scoredAt)}</span>
      </div>

      {open && (
        <div style={{ padding: '4px 10px 10px 36px', display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--m-border, #f0f0f0)' }}>
          {hasFixes ? (
            proposals.map((p) => (
              <ProposalRow key={p.id} proposal={p} showSite={false} onDismiss={() => onDismiss(p.id)} dismissing={dismissing(p.id)} />
            ))
          ) : (
            <div style={{ fontSize: 13, color: 'var(--m-text-muted, #6b7280)', paddingTop: 6 }}>
              No issues flagged from this conversation.
            </div>
          )}
          <Link href={`/conversations/${score.conversationId}`} style={{ fontSize: 12, color: 'var(--m-action, #4f46e5)', textDecoration: 'none' }}>
            View conversation →
          </Link>
        </div>
      )}
    </div>
  )
}

function ProposalRow({
  proposal,
  showSite,
  onDismiss,
  dismissing,
}: {
  proposal: Proposal
  showSite: boolean
  onDismiss: () => void
  dismissing: boolean
}) {
  const [applyOpen, setApplyOpen] = useState(false)

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--m-border, #e5e7eb)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span title={proposal.severity} style={{ width: 8, height: 8, borderRadius: '50%', background: severityDot(proposal.severity), marginTop: 6, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <Badge variant={proposal.dimension === 'grounding' ? 'error' : 'neutral'}>{DIMENSION_LABEL[proposal.dimension]}</Badge>
            {showSite && <span style={{ fontSize: 11, fontWeight: 600 }}>{siteMeta(proposal.site).name}</span>}
            <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>{proposal.source}</span>
            {proposal.occurrences > 1 && <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>×{proposal.occurrences}</span>}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.4 }}>{proposal.summary}</div>
          {proposal.targetField && (
            <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)', marginTop: 3 }}>
              target: <code style={{ fontFamily: 'ui-monospace, monospace' }}>{proposal.targetField}</code>
            </div>
          )}
          {proposal.proposedText && (
            <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)', marginTop: 3 }}>suggested: {proposal.proposedText}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Button variant={applyOpen ? 'outline' : 'primary'} onClick={() => setApplyOpen((o) => !o)}>
            {applyOpen ? 'Cancel' : 'Apply…'}
          </Button>
          {!applyOpen && <Button variant="outline" onClick={onDismiss} loading={dismissing}>Dismiss</Button>}
        </div>
      </div>
      {applyOpen && <ApplyPanel proposal={proposal} onDone={() => setApplyOpen(false)} />}
    </div>
  )
}

/**
 * The C2 approval surface: pick the target, edit the drafted text, see the
 * exact before/after, then apply. Nothing is written until "Approve & apply" —
 * and every apply snapshots the previous value server-side for one-click
 * revert (see the Recently-applied card).
 */
function ApplyPanel({ proposal, onDone }: { proposal: Proposal; onDone: () => void }) {
  const queryClient = useQueryClient()
  const validTarget = APPLY_TARGETS.some((t) => t.value === proposal.targetField)
  const [target, setTarget] = useState<string>(validTarget ? proposal.targetField! : 'knowledge')
  const [mode, setMode] = useState<'append' | 'replace'>('append')
  const [text, setText] = useState(proposal.proposedText ?? '')
  const isKnowledge = target === 'knowledge'

  const pbQuery = useQuery({
    queryKey: ['playbook', proposal.site],
    queryFn: () => getPlaybook(proposal.site),
    enabled: !isKnowledge,
  })
  const kQuery = useQuery({
    queryKey: ['knowledge', proposal.site],
    queryFn: () => getKnowledge(proposal.site),
    enabled: isKnowledge,
  })

  const loading = isKnowledge ? kQuery.isLoading : pbQuery.isLoading
  const current = isKnowledge
    ? (kQuery.data?.knowledgeText ?? '')
    : ((pbQuery.data?.playbook as Playbook | undefined)?.[target as keyof Playbook] ?? '')
  const effMode: 'append' | 'replace' = isKnowledge ? 'append' : mode
  const trimmed = text.trim()

  const apply = useMutation({
    mutationFn: () => applyProposal(proposal.id, { targetField: target, mode: effMode, text: trimmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] })
      queryClient.invalidateQueries({ queryKey: ['proposals-applied'] })
      queryClient.invalidateQueries({ queryKey: ['playbook', proposal.site] })
      queryClient.invalidateQueries({ queryKey: ['knowledge', proposal.site] })
      onDone()
    },
  })

  // Long docs: show only the tail of the current value so the join is visible.
  const TAIL = 500
  const currentTail = current.length > TAIL ? '…' + current.slice(-TAIL) : current
  const preStyle = {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    margin: 0,
    padding: 8,
    borderRadius: 6,
    border: '1px solid var(--m-border, #e5e7eb)',
    maxHeight: 160,
    overflowY: 'auto' as const,
    background: 'var(--m-bg-subtle, #f9fafb)',
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--m-border, #f0f0f0)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <label style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
          Target{' '}
          <select
            value={target}
            onChange={(e) => setTarget((e.target as HTMLSelectElement).value)}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 13, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
          >
            {APPLY_TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        {!isKnowledge && (
          <label style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
            Mode{' '}
            <select
              value={mode}
              onChange={(e) => setMode((e.target as HTMLSelectElement).value as 'append' | 'replace')}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 13, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
            >
              <option value="append">Append to section</option>
              <option value="replace">Replace section</option>
            </select>
          </label>
        )}
        {!validTarget && proposal.targetField && (
          <span style={{ fontSize: 11, color: 'var(--m-warn-ink, #b45309)' }}>
            suggested target “{proposal.targetField}” isn't a playbook field — defaulting to the knowledge doc
          </span>
        )}
      </div>

      <textarea
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
        rows={4}
        placeholder="The exact text to add — edit before applying. Only add facts you know to be true."
        style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid var(--m-border, #d1d5db)', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5 }}
      />

      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>Loading current value…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--m-text-muted, #6b7280)', marginBottom: 4 }}>
              CURRENT{isKnowledge && current.length > TAIL ? ' (end of doc)' : ''}
            </div>
            <pre style={preStyle}>{currentTail || <span style={{ color: 'var(--m-text-muted, #9ca3af)' }}>— empty —</span>}</pre>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--m-text-muted, #6b7280)', marginBottom: 4 }}>AFTER APPLY</div>
            <pre style={preStyle}>
              {effMode === 'append' && currentTail ? <>{currentTail}{'\n\n'}</> : null}
              <span style={{ background: 'rgba(21,128,61,.12)', borderRadius: 3 }}>{trimmed || '…'}</span>
            </pre>
          </div>
        </div>
      )}

      {apply.isError && (
        <Banner variant="error">{(apply.error as Error)?.message ?? "Couldn't apply the change."}</Banner>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!trimmed || loading}>
          Approve &amp; apply
        </Button>
        <span style={{ fontSize: 11, color: 'var(--m-text-muted, #6b7280)' }}>
          Goes live for the bot immediately · revert any time from “Recently applied”
        </span>
      </div>
    </div>
  )
}
